/**
 * Workflow History Atoms - Core state only
 *
 * Manages history state for workflows:
 * - History per workflow (isolated)
 * - Persistence in localStorage
 * - Max 20 actions per workflow
 *
 * Actions (undo/redo/addToHistory) are in actions/history-actions.ts
 */

import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import type { WorkflowListItem } from "@/isomorphic/schemas/workflow";
import type { Phase } from "@/isomorphic/schemas/phase";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Snapshot of workflow state at a point in time
 */
export interface WorkflowSnapshot {
  workflow: WorkflowListItem;
  phases: Phase[];
  timestamp: number;
}

/**
 * History for a single workflow
 */
export interface WorkflowHistory {
  states: WorkflowSnapshot[]; // Array of 1-20 states
  currentIndex: number; // Index of current state (-1 = not initialized)
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const MAX_HISTORY_SIZE = 20;

const createAtomCache = <T>(createAtom: (workflowId: string) => T) => {
  const cache = new Map<string, T>();

  return (workflowId: string): T => {
    const cached = cache.get(workflowId);
    if (cached) {
      return cached;
    }

    const nextAtom = createAtom(workflowId);
    cache.set(workflowId, nextAtom);
    return nextAtom;
  };
};

// ============================================================================
// STORAGE ATOMS (per workflow)
// ============================================================================

/**
 * Validate history format - if not valid, return empty history
 */
function validateHistoryFormat(data: unknown): WorkflowHistory {
  // Check if data has the required fields: states (array) and currentIndex (number)
  if (
    data &&
    typeof data === "object" &&
    "states" in data &&
    "currentIndex" in data &&
    Array.isArray(data.states) &&
    typeof data.currentIndex === "number"
  ) {
    return data as WorkflowHistory;
  }

  // If invalid format (old format or corrupted), reset to empty
  console.log("[History] Invalid or old format detected, resetting history");
  return {
    states: [],
    currentIndex: -1,
  };
}

/**
 * Custom storage with validation
 */
const createValidatedStorage = () => {
  const storage = createJSONStorage<WorkflowHistory>(() => localStorage);

  return {
    ...storage,
    getItem: (key: string, initialValue: WorkflowHistory) => {
      const stored = storage.getItem(key, initialValue);
      return validateHistoryFormat(stored);
    },
  };
};

/**
 * History atom family - one history atom per workflow
 * Persisted in localStorage with key: workflow-history-{workflowId}
 * Automatically resets history if format is invalid
 */
const buildWorkflowHistoryAtom = (workflowId: string) =>
  atomWithStorage<WorkflowHistory>(
    `workflow-history-${workflowId}`,
    {
      states: [],
      currentIndex: -1,
    },
    createValidatedStorage(),
    { getOnInit: true },
  );
export const workflowHistoryAtom = createAtomCache(buildWorkflowHistoryAtom);

// ============================================================================
// HELPER ATOMS
// ============================================================================

/**
 * Can undo for a specific workflow?
 */
export const canUndoAtomFamily = createAtomCache((workflowId: string) =>
  atom((get) => {
    const history = get(workflowHistoryAtom(workflowId));
    return history.currentIndex > 0;
  }),
);

/**
 * Can redo for a specific workflow?
 */
export const canRedoAtomFamily = createAtomCache((workflowId: string) =>
  atom((get) => {
    const history = get(workflowHistoryAtom(workflowId));
    // Safety check: ensure states array exists (migration from old format)
    if (!history.states || !Array.isArray(history.states)) {
      return false;
    }
    const canRedo = history.currentIndex < history.states.length - 1;
    console.log(
      `[History] canRedo check: currentIndex=${history.currentIndex}, statesLength=${history.states.length}, canRedo=${canRedo}`,
    );
    return canRedo;
  }),
);

/**
 * Number of past states (for undo)
 */
export const historyPastLengthAtomFamily = createAtomCache((workflowId: string) =>
  atom((get) => {
    const history = get(workflowHistoryAtom(workflowId));
    if (!history.states || !Array.isArray(history.states)) {
      return 0;
    }
    return history.currentIndex; // States before currentIndex
  }),
);

/**
 * Number of future states (for redo)
 */
export const historyFutureLengthAtomFamily = createAtomCache((workflowId: string) =>
  atom((get) => {
    const history = get(workflowHistoryAtom(workflowId));
    if (!history.states || !Array.isArray(history.states)) {
      return 0;
    }
    return history.states.length - 1 - history.currentIndex; // States after currentIndex
  }),
);
